import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../../utils/api";
import { useSettingsSection } from "./SettingsContext";
import { SettingsFieldSkeleton } from "./SettingsSkeleton";

export default function MaxItemsPerTicket({ stationId }) {
  const [maxItems, setMaxItems] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialRef = useRef(null);

  useEffect(() => {
    async function fetchSetting() {
      try {
        const res = await apiFetch(`/settings/max_items/${stationId}`);
        const data = await res.json();

        const value = parseInt(data.maxItems, 10);
        const safeValue = Number.isNaN(value) ? 1 : value;
        setMaxItems(safeValue);
        if (!initialRef.current) {
          initialRef.current = safeValue;
        }
      } catch (err) {
        console.error("Failed to load setting:", err);
        setMaxItems(1);
        if (!initialRef.current) {
          initialRef.current = 1;
        }
      } finally {
        setLoading(false);
      }
    }

    fetchSetting();
  }, [stationId]);

  const saveToStation = async (targetStationId, shouldUpdateInitial, overrideValue) => {
    const safeValue = parseInt(overrideValue ?? maxItems, 10);
    if (Number.isNaN(safeValue) || safeValue <= 0) {
      const err = new Error("Max items must be a number greater than 0.");
      err.code = "VALIDATION";
      throw err;
    }

    setSaving(true);
    try {
      await apiFetch(`/settings/max_items/${targetStationId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ maxItems: safeValue }),
      });
      if (shouldUpdateInitial) {
        initialRef.current = safeValue;
      }
    } catch (err) {
      console.error("Failed to save:", err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const getChanges = useCallback(() => {
    if (initialRef.current == null) return [];
    const current = parseInt(maxItems, 10);
    if (Number.isNaN(current)) return ["Max items per ticket: invalid value"];
    if (Number(initialRef.current) === Number(current)) return [];
    return [`Max items per ticket: ${initialRef.current} -> ${current}`];
  }, [maxItems]);

  useSettingsSection(
    "max-items",
    useMemo(
      () => ({
        getChanges,
        save: () => saveToStation(stationId, true),
        applyToStation: (targetStationId) => saveToStation(targetStationId, false),
        applySavedToStation: (targetStationId) => {
          if (initialRef.current == null) return Promise.resolve();
          return saveToStation(targetStationId, false, initialRef.current);
        },
      }),
      [getChanges, stationId, maxItems]
    )
  );

  return (
    <div className="mx-auto w-full max-w-2xl">
      <h3 className="mb-4 text-center text-lg font-semibold text-gray-800 dark:text-gray-200">
        Maximum Items Per Ticket Card
      </h3>

      {loading ? (
        <SettingsFieldSkeleton />
      ) : (
        <>
          <label className="mb-2 block text-center text-sm text-gray-700 dark:text-gray-300">
            Set the maximum number of items that may appear per ticket card, any items
            over this number will be split into a new ticket card:
          </label>
          <input
            type="number"
            value={maxItems}
            onChange={(e) => setMaxItems(e.target.value)}
            min={1}
            className="mb-4 w-full rounded-xl border px-4 py-3 text-base dark:bg-gray-700 dark:text-white"
          />
          {saving && (
            <div className="text-center text-xs text-gray-500 dark:text-gray-400">
              Saving...
            </div>
          )}
        </>
      )}
    </div>
  );
}
